import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  useCreatePreboardingForm,
  useDeletePreboardingForm,
  usePreboardingForms,
} from '@/hooks/use-preboarding-forms';
import { cn } from '@/lib/utils';
import type { PreboardingForm, PreboardingFormType } from '@hygge/shared';
import { Link, useNavigate } from '@tanstack/react-router';
import { AlertCircle, FileText, Loader2, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { FormTypeDialog } from './form-type-dialog';

const STATUS_BADGE: Record<string, string> = {
  DRAFT: 'text-muted-foreground border-border bg-muted/50',
  PUBLISHED: 'text-green-600 border-green-600/30 bg-green-600/5',
  ARCHIVED: 'text-amber-600 border-amber-600/30 bg-amber-600/5',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export function PreboardingFormsList() {
  const navigate = useNavigate();
  const { data: forms = [], isLoading, error } = usePreboardingForms();
  const createMutation = useCreatePreboardingForm();
  const deleteMutation = useDeletePreboardingForm();
  const [typeDialogOpen, setTypeDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PreboardingForm | null>(null);

  const handleCreate = async (type: PreboardingFormType) => {
    const form = await createMutation.mutateAsync({ type });
    setTypeDialogOpen(false);
    navigate({ to: '/settings/preboarding-forms/$id', params: { id: form.id } });
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    await deleteMutation.mutateAsync(deleteTarget.id);
    setDeleteTarget(null);
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">Pre-boarding Forms</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Build custom forms to collect documents and data from new hires before they join.
          </p>
        </div>
        <Button onClick={() => setTypeDialogOpen(true)}>
          <Plus className="mr-1.5 h-4 w-4" />
          New Form
        </Button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-24 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center gap-2 py-24 text-center">
          <AlertCircle className="h-6 w-6 text-destructive" />
          <p className="text-sm text-muted-foreground">Failed to load forms.</p>
        </div>
      ) : forms.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/20 py-20 text-center">
          <div className="mb-4 rounded-full bg-hygge/10 p-3">
            <FileText className="h-6 w-6 text-hygge" />
          </div>
          <h3 className="text-base font-semibold text-foreground">No forms yet</h3>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">
            Create your first pre-boarding form to start collecting new-hire information.
          </p>
          <Button className="mt-5" onClick={() => setTypeDialogOpen(true)}>
            <Plus className="mr-1.5 h-4 w-4" />
            New Form
          </Button>
        </div>
      ) : (
        <div className="space-y-2.5">
          {forms.map((form) => (
            <Card key={form.id} className="group transition-all hover:border-hygge/20 hover:shadow-sm">
              <div className="flex items-center gap-4 p-4">
                <Link
                  to="/settings/preboarding-forms/$id"
                  params={{ id: form.id }}
                  className="flex min-w-0 flex-1 items-center gap-3"
                >
                  <div className="rounded-lg bg-hygge/10 p-2">
                    <FileText className="h-5 w-5 text-hygge" />
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-medium text-foreground group-hover:text-hygge">
                      {form.name}
                    </p>
                    <p className="text-xs text-muted-foreground">Updated {formatDate(form.updatedAt)}</p>
                  </div>
                </Link>
                <Badge variant="outline" className={cn('text-[11px]', STATUS_BADGE[form.status])}>
                  {form.status}
                </Badge>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-muted-foreground hover:text-destructive"
                  onClick={() => setDeleteTarget(form)}
                  aria-label={`Delete ${form.name}`}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}

      <FormTypeDialog
        open={typeDialogOpen}
        onOpenChange={setTypeDialogOpen}
        onConfirm={handleCreate}
        isCreating={createMutation.isPending}
      />

      <AlertDialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open && !deleteMutation.isPending) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete form?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes{' '}
              <span className="font-medium text-foreground">{deleteTarget?.name}</span> and its
              configuration. This can&apos;t be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void handleDelete();
              }}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete form
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
